import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import {
  GraduationCap, SignOut, UploadSimple, Sparkle, FloppyDisk, Trash,
  CheckCircle, Plus, Lightning, FileText, ArrowClockwise,
} from "@phosphor-icons/react";

const normKey = (n) => (n || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const emptyLine = () => ({ sku: "", rev: "", description: "", quantity_ordered: "", unit_price: "", uom: "", due_date: "" });

export default function POTraining() {
  const { user, logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [profile, setProfile] = useState(null);
  const [rulesText, setRulesText] = useState("");
  const [domainsText, setDomainsText] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [poIdx, setPoIdx] = useState(0);
  const [draft, setDraft] = useState(null);
  const [savingGolden, setSavingGolden] = useState(false);

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try { const { data } = await api.get("/training/customers"); setCustomers(data || []); }
    catch { /* ignore */ }
  };

  const loadProfile = async (name) => {
    const key = normKey(name);
    if (!key) { setProfile(null); setRulesText(""); setDomainsText(""); return; }
    try {
      const { data } = await api.get(`/training/profiles/${key}`);
      setProfile(data);
      setRulesText((data.rules || []).join("\n"));
      setDomainsText((data.sender_domains || []).join(", "));
    } catch {
      setProfile(null); setRulesText(""); setDomainsText("");
    }
  };

  const pickCustomer = (name) => { setCustomerName(name); loadProfile(name); };

  const saveProfile = async () => {
    if (!customerName.trim()) { toast.error("Enter or pick a customer first"); return; }
    setSavingProfile(true);
    try {
      const rules = rulesText.split("\n").map((r) => r.trim()).filter(Boolean);
      const sender_domains = domainsText.split(",").map((d) => d.trim()).filter(Boolean);
      const { data } = await api.put("/training/profiles", { customer_name: customerName.trim(), rules, sender_domains });
      setProfile(data);
      toast.success("Profile saved — rules & domains will apply to this customer's POs");
      loadCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save profile"); }
    finally { setSavingProfile(false); }
  };

  const applyResult = (data) => {
    setResult(data);
    setPoIdx(0);
    const first = (data.pos && data.pos[0]) ? data.pos[0] : null;
    setDraft(first ? normalizeDraft(first) : null);
  };

  const parse = async () => {
    if (!file && !pasteText.trim()) { toast.error("Upload a PO file or paste PO text"); return; }
    setParsing(true); setResult(null); setDraft(null);
    try {
      let data;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("customer_name", customerName.trim());
        ({ data } = await api.post("/training/parse", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post("/training/parse-text", { text: pasteText, customer_name: customerName.trim() || null }));
      }
      applyResult(data);
      const n = (data.pos || []).length;
      toast.success(`Read ${n} PO${n === 1 ? "" : "s"} · method: ${data.method}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the PO"); }
    finally { setParsing(false); }
  };

  const selectPo = (i) => {
    setPoIdx(i);
    const po = result?.pos?.[i];
    setDraft(po ? normalizeDraft(po) : null);
  };

  const saveGolden = async () => {
    if (!customerName.trim()) { toast.error("Pick/enter the customer this PO belongs to"); return; }
    if (!draft) { toast.error("Nothing to save"); return; }
    setSavingGolden(true);
    try {
      const corrected = draftToJson(draft);
      const src = (result?.pos?.[poIdx]?.source_excerpt) || result?.raw_text || pasteText || "";
      const { data } = await api.post("/training/golden", {
        customer_name: customerName.trim(),
        source_text: src,
        corrected_json: corrected,
        format_signature: result?.format_signature || null,
        label: result?.filename || null,
        source_filename: result?.filename || null,
      });
      setProfile(data);
      setDomainsText((data.sender_domains || []).join(", "));
      setRulesText((data.rules || []).join("\n"));
      toast.success("Saved as a verified example — the reader will now match this format");
      loadCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save example"); }
    finally { setSavingGolden(false); }
  };

  const deleteGolden = async (exampleId) => {
    const key = normKey(customerName);
    try {
      await api.delete(`/training/golden/${key}/${exampleId}`);
      toast.success("Example removed");
      loadProfile(customerName);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const filteredCustomers = useMemo(() => {
    const q = customerName.trim().toLowerCase();
    if (!q) return customers.slice(0, 40);
    return customers.filter((c) => c.toLowerCase().includes(q)).slice(0, 40);
  }, [customers, customerName]);

  return (
    <div style={{ minHeight: "100vh", background: "#F1F5F9" }} data-testid="po-training-page">
      {/* Header */}
      <div style={{ background: "#0F172A", color: "white", padding: "14px 26px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <GraduationCap size={26} weight="duotone" color="#5EEAD4" />
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.3 }}>PO Reading — Training Studio</div>
            <div style={{ fontSize: 12, color: "#94A3B8" }}>Teach the reader how each customer's purchase orders should be understood</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 13, color: "#CBD5E1" }}>{user?.username}</span>
          <button data-testid="training-logout" onClick={logout} className="omh-icon-btn" title="Sign out" style={{ color: "white" }}>
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 20, padding: 24, maxWidth: 1500, margin: "0 auto" }}>
        {/* LEFT — customer + profile */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Panel title="1 · Choose customer">
            <input
              data-testid="training-customer-input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              onBlur={() => customerName && loadProfile(customerName)}
              placeholder="Type or pick a customer…"
              style={inputStyle}
            />
            <div style={{ maxHeight: 190, overflowY: "auto", marginTop: 8, border: "1px solid #E2E8F0", borderRadius: 8 }}>
              {filteredCustomers.length === 0 && <div style={{ padding: 10, fontSize: 12, color: "#64748B" }}>No matches — type a new name to create a profile.</div>}
              {filteredCustomers.map((c) => (
                <button key={c} data-testid={`training-cust-${normKey(c)}`} onClick={() => pickCustomer(c)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", borderBottom: "1px solid #F1F5F9",
                    background: normKey(c) === normKey(customerName) ? "#ECFDF5" : "white", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
                  {c}
                </button>
              ))}
            </div>
          </Panel>

          <Panel title="2 · Customer rules (optional)" subtitle="Plain-English hints, one per line">
            <textarea data-testid="training-rules" value={rulesText} onChange={(e) => setRulesText(e.target.value)} rows={5}
              placeholder={"PO number follows the label 'Order Ref'\nDates are DD/MM/YYYY\nIgnore the freight/handling line"}
              style={{ ...inputStyle, fontFamily: "Inter, sans-serif", fontSize: 12.5, resize: "vertical" }} />
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 12, display: "block" }}>Sender email domains</label>
            <input data-testid="training-domains" value={domainsText} onChange={(e) => setDomainsText(e.target.value)} placeholder="acme.com, buyer.acme.com" style={{ ...inputStyle, marginTop: 4 }} />
            <button data-testid="training-save-profile" onClick={saveProfile} disabled={savingProfile}
              style={{ ...btnPrimary, marginTop: 12, width: "100%" }}>
              <FloppyDisk size={15} weight="bold" /> {savingProfile ? "Saving…" : "Save profile"}
            </button>
          </Panel>

          <Panel title="3 · Verified examples" subtitle={`${profile?.golden_examples?.length || 0} saved`}>
            {(!profile || !profile.golden_examples?.length) && <div style={{ fontSize: 12.5, color: "#64748B" }}>No examples yet. Parse a PO on the right, correct it, then <strong>Save as verified example</strong>.</div>}
            {profile?.golden_examples?.map((ex) => (
              <div key={ex.id} data-testid={`training-golden-${ex.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, marginBottom: 8, background: "white" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <FileText size={13} weight="bold" style={{ marginRight: 5, verticalAlign: -2 }} />{ex.label}
                  </div>
                  <div style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDate(ex.created_at)} · sig {String(ex.format_signature || "").slice(0, 8)}</div>
                </div>
                <button data-testid={`training-golden-del-${ex.id}`} onClick={() => deleteGolden(ex.id)} className="omh-icon-btn" title="Remove" style={{ color: "#DC2626", flexShrink: 0 }}>
                  <Trash size={15} weight="bold" />
                </button>
              </div>
            ))}
          </Panel>
        </div>

        {/* RIGHT — parse + correct */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Panel title="4 · Feed a sample PO">
            <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
              <label data-testid="training-file-label" style={{ flex: "1 1 240px", border: "2px dashed #CBD5E1", borderRadius: 10, padding: "16px", textAlign: "center", cursor: "pointer", background: file ? "#ECFDF5" : "white" }}>
                <UploadSimple size={22} weight="bold" color="#0F766E" />
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>{file ? file.name : "Upload PO (PDF, image, Excel, DOCX, EML)"}</div>
                <input data-testid="training-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xlsm,.docx,.rtf,.eml,.txt" style={{ display: "none" }}
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setPasteText(""); }} />
              </label>
              <textarea data-testid="training-paste" value={pasteText} onChange={(e) => { setPasteText(e.target.value); setFile(null); }}
                placeholder="…or paste the PO email/text here" rows={4} style={{ ...inputStyle, flex: "1 1 240px", resize: "vertical", fontSize: 12.5 }} />
            </div>
            <button data-testid="training-parse-btn" onClick={parse} disabled={parsing} style={{ ...btnPrimary, marginTop: 12 }}>
              <Sparkle size={15} weight="fill" /> {parsing ? "Reading…" : "Read PO"}
            </button>
            {result?.applied && (
              <span data-testid="training-applied-pill" style={{ marginLeft: 12, fontSize: 12, fontWeight: 700, color: "#0F766E" }}>
                <Lightning size={13} weight="fill" style={{ verticalAlign: -2 }} /> Applied {result.applied.rules_count} rule(s) + {result.applied.examples_count} example(s)
              </span>
            )}
            {result && !result.applied && (
              <span style={{ marginLeft: 12, fontSize: 12, color: "#64748B" }}>No training profile applied (generic read)</span>
            )}
          </Panel>

          {result && draft && (
            <Panel title="5 · Review & correct" subtitle={result.pos.length > 1 ? `${result.pos.length} POs found` : "Fix any wrong fields, then save"}>
              {result.pos.length > 1 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                  {result.pos.map((p, i) => (
                    <button key={i} onClick={() => selectPo(i)} data-testid={`training-po-tab-${i}`}
                      style={{ padding: "5px 12px", borderRadius: 999, border: "1px solid #CBD5E1", cursor: "pointer",
                        background: i === poIdx ? "#0F172A" : "white", color: i === poIdx ? "white" : "#0F172A", fontSize: 12, fontWeight: 700 }}>
                      {p.po_number || `PO ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
                <Field label="PO Number" testid="f-po-number" value={draft.po_number} onChange={(v) => setDraft({ ...draft, po_number: v })} />
                <Field label="Customer" testid="f-customer" value={draft.customer_name} onChange={(v) => setDraft({ ...draft, customer_name: v })} />
                <Field label="Order Date" testid="f-order-date" value={draft.order_date} onChange={(v) => setDraft({ ...draft, order_date: v })} />
                <Field label="Requested Ship Date" testid="f-ship-date" value={draft.requested_ship_date} onChange={(v) => setDraft({ ...draft, requested_ship_date: v })} />
                <Field label="Currency" testid="f-currency" value={draft.currency} onChange={(v) => setDraft({ ...draft, currency: v })} />
                <Field label="Total Amount" testid="f-total" value={draft.total_amount} onChange={(v) => setDraft({ ...draft, total_amount: v })} />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, margin: "16px 0 6px" }}>Line items</div>
              <div style={{ overflowX: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="training-lines">
                  <thead>
                    <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                      {["SKU", "Rev", "Description", "Qty", "Price", "UOM", "Due Date", ""].map((h) => (
                        <th key={h} style={{ padding: "6px 8px", fontSize: 11, color: "#64748B", fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {draft.line_items.map((ln, i) => (
                      <tr key={i} data-testid={`training-line-${i}`}>
                        {["sku", "rev", "description", "quantity_ordered", "unit_price", "uom", "due_date"].map((k) => (
                          <td key={k} style={{ padding: 3, borderTop: "1px solid #F1F5F9" }}>
                            <input value={ln[k] ?? ""} data-testid={`training-line-${i}-${k}`}
                              onChange={(e) => {
                                const items = [...draft.line_items];
                                items[i] = { ...items[i], [k]: e.target.value };
                                setDraft({ ...draft, line_items: items });
                              }}
                              style={{ width: k === "description" ? 220 : (k === "quantity_ordered" || k === "unit_price" ? 70 : 90), border: "1px solid #E2E8F0", borderRadius: 5, padding: "4px 6px", fontSize: 12 }} />
                          </td>
                        ))}
                        <td style={{ padding: 3, borderTop: "1px solid #F1F5F9" }}>
                          <button onClick={() => setDraft({ ...draft, line_items: draft.line_items.filter((_, j) => j !== i) })}
                            className="omh-icon-btn" title="Remove line" data-testid={`training-line-del-${i}`} style={{ color: "#DC2626" }}>
                            <Trash size={13} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button data-testid="training-add-line" onClick={() => setDraft({ ...draft, line_items: [...draft.line_items, emptyLine()] })}
                style={{ ...btnGhost, marginTop: 8 }}>
                <Plus size={13} weight="bold" /> Add line
              </button>

              <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "center" }}>
                <button data-testid="training-save-golden" onClick={saveGolden} disabled={savingGolden} style={btnPrimary}>
                  <CheckCircle size={15} weight="bold" /> {savingGolden ? "Saving…" : "Save as verified example"}
                </button>
                <button data-testid="training-reparse" onClick={parse} disabled={parsing} style={btnGhost}>
                  <ArrowClockwise size={14} weight="bold" /> Re-read
                </button>
              </div>
            </Panel>
          )}

          {result && (
            <Panel title="Source text read" subtitle={`fingerprint ${String(result.format_signature || "—").slice(0, 10)}`}>
              <pre data-testid="training-raw-text" style={{ maxHeight: 240, overflow: "auto", background: "#0F172A", color: "#E2E8F0", padding: 14, borderRadius: 8, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
                {result.raw_text || result.pos?.[poIdx]?.source_excerpt || "(image-based PO — no text layer)"}
              </pre>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function normalizeDraft(po) {
  return {
    po_number: po.po_number ?? "",
    customer_name: po.customer_name ?? "",
    order_date: po.order_date ?? "",
    requested_ship_date: po.requested_ship_date ?? "",
    ship_to_address: po.ship_to_address ?? "",
    bill_to_address: po.bill_to_address ?? "",
    currency: po.currency ?? "USD",
    total_amount: po.total_amount ?? "",
    notes: po.notes ?? "",
    line_items: (po.line_items || []).map((l) => ({
      sku: l.sku ?? "", rev: l.rev ?? "", description: l.description ?? "",
      quantity_ordered: l.quantity_ordered ?? "", unit_price: l.unit_price ?? "",
      uom: l.uom ?? "", due_date: l.due_date ?? "",
    })),
  };
}

function draftToJson(d) {
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
  const s = (v) => { const t = (v ?? "").toString().trim(); return t === "" ? null : t; };
  return {
    po_number: s(d.po_number) || "",
    customer_name: s(d.customer_name) || "",
    order_date: s(d.order_date),
    requested_ship_date: s(d.requested_ship_date),
    ship_to_address: s(d.ship_to_address),
    bill_to_address: s(d.bill_to_address),
    currency: s(d.currency) || "USD",
    total_amount: num(d.total_amount),
    notes: s(d.notes),
    line_items: d.line_items.map((l) => ({
      sku: s(l.sku), rev: s(l.rev), description: s(l.description) || "",
      quantity_ordered: num(l.quantity_ordered) ?? 0, unit_price: num(l.unit_price),
      uom: s(l.uom), due_date: s(l.due_date),
    })),
    confidence: 1.0,
    extraction_notes: "Verified by trainer",
  };
}

const inputStyle = { width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "9px 11px", fontSize: 13, outline: "none", boxSizing: "border-box" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", border: "none", borderRadius: 8, background: "#0F766E", color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer" };
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1px solid #CBD5E1", borderRadius: 8, background: "white", color: "#0F172A", fontSize: 12.5, fontWeight: 700, cursor: "pointer" };

function Panel({ title, subtitle, children }) {
  return (
    <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, boxShadow: "0 1px 3px rgba(2,8,23,.05)" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: "#0F172A", letterSpacing: 0.2 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 600 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</label>
      <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
    </div>
  );
}
